import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { makeOwnerGate } from '../lib/ownerGate';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { BrandLoader } from '../components/BrandLoader';
import { studiesApi, specialtiesApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, FlaskConical, FileCheck, Clock, CheckCircle2, XCircle, AlertCircle, Building2, Loader2 } from 'lucide-react';

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-blue-100 text-blue-700',
  ARCHIVED: 'bg-slate-100 text-slate-500',
};

const REQUEST_STATUS_DOT = {
  SUBMITTED: 'bg-blue-500', ROUTED_TO_CONSULTANTS: 'bg-teal-500', IN_PROGRESS: 'bg-yellow-500',
  COMPLETED: 'bg-emerald-500', EXECUTED: 'bg-emerald-600', FAILED: 'bg-red-500',
  REJECTED: 'bg-red-500', CHANGES_REQUESTED: 'bg-amber-500', WITHDRAWN: 'bg-slate-400',
  AWAITING_CHAIRMAN_ASSIGNMENT: 'bg-blue-400', COORDINATOR_ASSIGNED: 'bg-violet-500',
  UNDER_REVIEW: 'bg-yellow-600', PENDING_FINAL_APPROVAL: 'bg-green-400',
  PENDING_DIRECTOR_REVIEW: 'bg-indigo-500', PENDING_EXEC_DIRECTOR_REVIEW: 'bg-indigo-600',
  PENDING_SIGNATURES: 'bg-purple-500', RESUBMITTED: 'bg-blue-400',
};

export default function StudiesList() {
  const { isCompanyRep, isCompanyOwner } = useAuth();
  const blockIfOwner = makeOwnerGate(isCompanyOwner);
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchStudies();
  }, []);

  const fetchStudies = async () => {
    try {
      const res = await studiesApi.getAll();
      setStudies(res.data);
    } catch (error) {
      toast.error('Failed to load studies');
    } finally {
      setLoading(false);
    }
  };

  const filtered = studies.filter(s => {
    const matchesSearch = !search || s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.therapeutic_area?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatStatus = (status) => status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  if (loading) {
    return (
      <DashboardLayout title="Studies">
        <div className="flex items-center justify-center h-64">
          <BrandLoader size="md" label="Loading studies…" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Studies">
      {/* iter164c — Restructured to match the Requests page layout: full-width
          container, Card-wrapped filter row, icon-led list rows (matches
          RequestsList density so the two pages feel like siblings). */}
      <div className="space-y-6" data-testid="studies-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">Studies</h2>
            <p className="text-slate-500 mt-1">
              {filtered.length} {filtered.length === 1 ? 'study' : 'studies'} found
            </p>
          </div>
          {isCompanyRep() && (
            <Button onClick={() => { if (blockIfOwner()) return; navigate('/studies/new'); }} className="bg-blue-900 hover:bg-blue-950" data-testid="new-study-btn">
              <Plus className="w-4 h-4 mr-2" /> New Study
            </Button>
          )}
        </div>

        {/* Filters (matches RequestsList shell) */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by title, therapeutic area..."
                    className="pl-10"
                    data-testid="study-search"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="study-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="ON_HOLD">On Hold</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
              {(search || statusFilter !== 'all') && (
                <Button
                  variant="ghost"
                  onClick={() => { setSearch(''); setStatusFilter('all'); }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Studies List */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FlaskConical className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No studies found</h3>
              <p className="text-slate-500 mt-1">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : (isCompanyRep() ? 'Create your first study to get started' : 'Studies will appear here once created')}
              </p>
              {isCompanyRep() && !search && statusFilter === 'all' && (
                <Button
                  onClick={() => { if (blockIfOwner()) return; navigate('/studies/new'); }}
                  className="mt-4 bg-blue-900 hover:bg-blue-950"
                >
                  Create Study
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filtered.map((study, index) => (
              <Card
                key={study.id}
                className="card-hover animate-slide-up min-w-0 overflow-hidden cursor-pointer"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/studies/${study.id}`)}
                data-testid={`study-card-${study.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 min-w-0">
                    {/* Icon block — matches RequestsList visual rhythm */}
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <FlaskConical className="w-6 h-6 text-indigo-600" />
                    </div>

                    {/* Main column */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 min-w-0">
                        <Badge variant="outline" className="text-[10px] font-semibold uppercase tracking-wide flex-shrink-0">
                          Study
                        </Badge>
                        <span className="text-sm font-mono text-slate-500 truncate">
                          {study.request_count} {study.request_count === 1 ? 'request' : 'requests'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-900 truncate">{study.title}</h3>
                      {study.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2 break-words">
                          {study.description}
                        </p>
                      )}

                      {/* Request pills (small, inline) */}
                      {study.requests_summary && study.requests_summary.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2 min-w-0">
                          {study.requests_summary.slice(0, 5).map((req) => (
                            <div key={req.id} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-[11px] max-w-full">
                              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${REQUEST_STATUS_DOT[req.status] || 'bg-slate-400'}`} />
                              <span className="font-medium text-slate-700 flex-shrink-0">{req.type}</span>
                              <span className="text-slate-500 truncate">{formatStatus(req.status)}</span>
                            </div>
                          ))}
                          {study.requests_summary.length > 5 && (
                            <span className="text-[10px] text-slate-400 self-center">
                              +{study.requests_summary.length - 5} more
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 flex-wrap min-w-0">
                        {study.company_name && (
                          <span className="flex items-center gap-1 min-w-0 max-w-full">
                            <Building2 size={12} className="flex-shrink-0" />
                            <span className="truncate">{study.company_name}</span>
                          </span>
                        )}
                        {study.specialty_names && study.specialty_names.length > 0 && (
                          <span className="flex items-center gap-1 min-w-0 max-w-full">
                            <FlaskConical size={12} className="flex-shrink-0" />
                            <span className="truncate">{study.specialty_names.join(', ')}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right rail: status badge */}
                    <div className="text-right flex-shrink-0">
                      <Badge className={`${STATUS_COLORS[study.status] || 'bg-slate-100 text-slate-500'}`}>
                        {formatStatus(study.status)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
